import {
  AbilityBuilder,
  createMongoAbility,
  MongoAbility,
} from "@casl/ability";

// Define action types
export type Actions = "create" | "read" | "update" | "delete" | "manage";

// Define subject types - add more as needed
export type Subjects =
  | "all"
  | "User"
  | "Role"
  | "TitleConfiguration"
  | "InvoiceParty"
  | "PartnerChannel"
  | "Publisher"
  | "Campaigns"
  | "RoleManagement"
  | "TriggerPush";

// Define user roles
export type UserRole = "admin" | "editor" | "viewer";

// Define ability type
export type AppAbility = MongoAbility<[Actions, Subjects]>;

// Define editable modules for editor role
const MODULES: Subjects[] = [
  "InvoiceParty",
  "Publisher",
  "TitleConfiguration",
  "PartnerChannel",
  "RoleManagement",
];

// Define user interface
export interface User {
  id: string;
  role: UserRole;
  email?: string;
  name?: string;
  googleId?: string;
}

// Create ability based on user role
export function defineAbilityFor(user: User | null): AppAbility {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(
    createMongoAbility,
  );

  if (!user) {
    // No user - no permissions
    return build();
  }

  switch (user.role) {
    case "admin":
      // Admin has all permissions
      can("manage", "all");
      break;

    case "editor":
      // Read permissions
      can("read", "all");

      // Create, update, delete for editable modules
      can(["create", "update", "delete"], MODULES);

      // Cannot manage users and roles
      cannot(["create", "update", "delete"], ["User", "Role"]);
      break;

    case "viewer":
    default:
      // Viewer can only read
      can("read", "all");
      break;
  }

  return build();
}

// Helper function to check if user has specific permission
// Note: This recreates the ability on each call. For frequent checks,
// consider using the useAbility hook which maintains a single instance.
export function canPerformAction(
  user: User | null,
  action: Actions,
  subject: Subjects,
): boolean {
  const ability = defineAbilityFor(user);
  return ability.can(action, subject);
}
